import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface MemberDebt {
  id: string;
  user_id: string;
  gym_id: string;
  category_id: string | null;
  category_name: string;
  category_validity_days: number;
  total_amount: number;
  paid_amount: number;
  owed_amount: number;
  grace_days: number;
  due_date: string; // YYYY-MM-DD
  status: "active" | "settled";
  started_at: string;
  settled_at: string | null;
  settlement_payment_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type DebtVisualStatus = "active" | "overdue";

export interface DebtInfo {
  debt: MemberDebt;
  visualStatus: DebtVisualStatus;
}

const computeVisualStatus = (debt: MemberDebt): DebtVisualStatus => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(debt.due_date + "T00:00:00");
  return due < today ? "overdue" : "active";
};

/** Active debts for a list of users (Map<userId, DebtInfo>). */
export const useUserDebts = (gymId: string | null | undefined, userIds: string[]) => {
  return useQuery({
    queryKey: ["member-debts-by-users", gymId, userIds],
    queryFn: async (): Promise<Map<string, DebtInfo>> => {
      const map = new Map<string, DebtInfo>();
      if (!gymId || userIds.length === 0) return map;

      const chunkSize = 200;
      for (let i = 0; i < userIds.length; i += chunkSize) {
        const chunk = userIds.slice(i, i + chunkSize);
        const { data, error } = await supabase
          .from("member_debts")
          .select("*")
          .eq("gym_id", gymId)
          .eq("status", "active")
          .in("user_id", chunk);
        if (error) throw error;
        (data || []).forEach((d) => {
          const debt = d as MemberDebt;
          if (!map.has(debt.user_id)) {
            map.set(debt.user_id, { debt, visualStatus: computeVisualStatus(debt) });
          }
        });
      }
      return map;
    },
    enabled: !!gymId && userIds.length > 0,
  });
};

/** All active debts for a gym (used by DebtorsPanel). */
export const useGymDebtors = (gymId: string | null | undefined) => {
  return useQuery({
    queryKey: ["gym-debtors", gymId],
    queryFn: async () => {
      if (!gymId) return [] as Array<MemberDebt & { displayName: string; visualStatus: DebtVisualStatus }>;

      const { data, error } = await supabase
        .from("member_debts")
        .select("*")
        .eq("gym_id", gymId)
        .eq("status", "active")
        .order("due_date", { ascending: true });
      if (error) throw error;

      const debts = (data || []) as MemberDebt[];
      const userIds = Array.from(new Set(debts.map((d) => d.user_id)));
      if (userIds.length === 0) return [];

      const [profilesRes, pendingRes] = await Promise.all([
        supabase.from("profiles").select("id, first_name, last_name, username").in("id", userIds),
        supabase.from("pending_members").select("id, first_name, last_name").in("id", userIds),
      ]);

      const nameMap = new Map<string, string>();
      (profilesRes.data || []).forEach((p: any) => {
        const name = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.username || "Sin nombre";
        nameMap.set(p.id, name);
      });
      (pendingRes.data || []).forEach((p: any) => {
        if (!nameMap.has(p.id)) {
          const name = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Pendiente";
          nameMap.set(p.id, name);
        }
      });

      return debts.map((d) => ({
        ...d,
        displayName: nameMap.get(d.user_id) || "Sin nombre",
        visualStatus: computeVisualStatus(d),
      }));
    },
    enabled: !!gymId,
  });
};
